import Vuex from 'vuex';
import Requests from '@/requests/requests';
import Swal from 'sweetalert2/dist/sweetalert2.min';

import router from '@/router/index';
import loadGoogleAPI from '@/libs/googleAPI';

import {
  BLOCK_UI, RELEASE_UI, SET_GOOGLE_API,
  SET_USER, UNSET_USER, SET_USER_KEY,
  SET_SESSION_TIMEOUT, UNSET_SESSION_TIMEOUT,
  SET_UNAUTH_REDIRECT, UNSET_UNAUTH_REDIRECT,
  SET_CAMPAIGN_URL,
} from '@/vuex/types/mutations';

import {
  BLOCK_INTERFACE, RELEASE_INTERFACE,
  ENABLE_GOOGLE_API, DISABLE_GOOGLE_API,
  DEFINE_USER, CLEAR_USER, DEFINE_USER_PICTURE_PROFILE,
  LOGIN, LOGOUT, RECOVER_DATA,
  RESTART_SESSION, ALERT_SESSION_ENDING,
  DEFINE_SESSION_TIMEOUT, CLEAR_SESSION_TIMEOUT,
  DEFINE_UNAUTH_REDIRECT, CLEAR_UNAUTH_REDIRECT,
  DEFINE_CAMPAIGN_URL,
} from '@/vuex/types/actions';

import { DEFINE_ALERTS, CLEAR_ALERTS } from '@/vuex/modules/alerts/types/actions';
import { DEFINE_DOCUMENTS, CLEAR_DOCUMENTS } from '@/vuex/modules/documents/types/actions';
import { DEFINE_ENTITIES, CLEAR_ENTITIES } from '@/vuex/modules/entities/types/actions';
import { DEFINE_MISSING_INDICATORS, CLEAR_MISSING_INDICATORS } from '@/vuex/modules/missing/types/actions';
import { DEFINE_NOTIFICATIONS, CLEAR_NOTIFICATIONS } from '@/vuex/modules/notifications/types/actions';
import { DEFINE_PROFILE, CLEAR_PROFILE } from '@/vuex/modules/profile/types/actions';
import { DEFINE_PAYMENT_METHODS, CLEAR_PAYMENT_METHODS } from '@/vuex/modules/payments/types/actions';
import { DEFINE_PERMISSIONS, CLEAR_PERMISSIONS } from '@/vuex/modules/permissions/types/actions';
import { DEFINE_ACCOUNT_EXECUTIVE, CLEAR_ACCOUNT_EXECUTIVE } from '@/vuex/modules/accountExecutive/types/actions';


import Alerts from '@/vuex/modules/alerts';
import Documents from '@/vuex/modules/documents';
import EntitiesModule from '@/vuex/modules/entities';
import MissingIndicators from '@/vuex/modules/missing';
import PaymentMethods from '@/vuex/modules/payments';
import ProfileModule from '@/vuex/modules/profile';
import NotificationsModule from '@/vuex/modules/notifications';
import PermissionsModule from '@/vuex/modules/permissions';
import AccountExecutiveModule from '@/vuex/modules/accountExecutive';

function createVuexStore() {
  return new Vuex.Store({
    strict: process.env.NODE_ENV === 'development',
    state: {
      loadedGoogleAPI: false,
      user: null,
      timeoutId: null,
      unauthRedirect: null,
      sessionTime: {
        max: 15 * 60 * 1000,
        toDecide: 30 * 1000,
      },
      uiBlocker: {
        message: 'Please Wait...',
        displayLoader: true,
        block: false,
      },
      campaignUrl: null,
    },
    modules: {
      alerts: Alerts,
      documents: Documents,
      entities: EntitiesModule,
      missing: MissingIndicators,
      profile: ProfileModule,
      paymentMethods: PaymentMethods,
      notifications: NotificationsModule,
      permissions: PermissionsModule,
      accountExecutive: AccountExecutiveModule,
    },
    getters: {
      loadedGoogleAPI: state => state.loadedGoogleAPI,
      user: state => state.user,
      hasAuthenticatedUser: state => ![null, undefined, {}, [], ''].includes(state.user),
      hasTeamsAreaRequired: state => state.user && state.user.teamsAreaRequired,
      blockUIData: state => state.uiBlocker,
      timeoutId: state => state.timeoutId,
      sessionTime: state => state.sessionTime,
      unauthRedirect: state => state.unauthRedirect,
      campaignUrl: state => state.campaignUrl,
    },
    mutations: {
      [SET_GOOGLE_API](state, loaded) {
        state.loadedGoogleAPI = loaded;
      },
      [SET_USER](state, userPayload) {
        const userData = {};
        userData.firstName = userPayload.first_name;
        userData.lastName = userPayload.last_name;
        userData.phone = userPayload.phone_number;
        userData.email = userPayload.user_email;
        userData.username = userPayload.user_email;
        userData.id = userPayload.id;
        userData.lastLogin = new Date();
        userData.created = new Date(userPayload.created);
        userData.optIn = userPayload.email_opt_in;
        userData.verified = userPayload.verified;
        userData.borrower = userPayload.borrower;
        userData.picture = userPayload.picture;
        userData.teamsAreaRequired = userPayload.teams_area_required;
        state.user = userData;

        /* eslint no-param-reassign: ["error", { "props": false }] */
        userPayload.lastLogin = userData.lastLogin;
        localStorage.user = JSON.stringify(userPayload);
      },
      [UNSET_USER](state) {
        state.user = null;
        localStorage.removeItem('user');
      },
      [SET_USER_KEY](state, payload) {
        const key = payload.key;
        const data = payload.data;

        const user = state.user;
        user[key] = data;
        state.user = user;

        const localUser = JSON.parse(localStorage.user);
        localUser[key] = data;
        localStorage.user = JSON.stringify(localUser);
      },
      [BLOCK_UI](state, payload) {
        const newBlockerState = {
          message: typeof payload.message === 'string' ? payload.message : 'Please Wait...',
          displayLoader: typeof payload.displayLoader === 'boolean' ? payload.displayLoader : true,
          block: true,
        };
        state.uiBlocker = newBlockerState;
      },
      [RELEASE_UI](state) {
        state.uiBlocker.message = 'Please Wait...';
        state.uiBlocker.displayLoader = true;
        state.uiBlocker.block = false;
      },
      [SET_SESSION_TIMEOUT](state, timeoutId) {
        state.timeoutId = timeoutId;
        localStorage.timeoutId = timeoutId;
      },
      [UNSET_SESSION_TIMEOUT](state) {
        clearTimeout(state.timeoutId || localStorage.timeoutId);
        state.timeoutId = null;
        localStorage.timeoutId = null;
      },
      [SET_UNAUTH_REDIRECT](state, next) {
        state.unauthRedirect = next;
      },
      [UNSET_UNAUTH_REDIRECT](state) {
        state.unauthRedirect = null;
      },
      [SET_CAMPAIGN_URL](state, url) {
        state.campaignUrl = url;
      },
    },
    actions: {
      [ENABLE_GOOGLE_API](context) {
        if (context.getters.loadedGoogleAPI) { return; }
        loadGoogleAPI
          .then(() => context.commit(SET_GOOGLE_API, true))
          .catch(() => { });
      },
      [DISABLE_GOOGLE_API](context) {
        context.commit(SET_GOOGLE_API, false);
      },
      [BLOCK_INTERFACE]({ commit }, payload) {
        commit(BLOCK_UI, payload || {});
      },
      [RELEASE_INTERFACE]({ commit }) {
        commit(RELEASE_UI);
      },
      [CLEAR_USER](context) {
        context.commit(UNSET_USER);
        context.dispatch(CLEAR_SESSION_TIMEOUT);
      },
      [DEFINE_USER](context, payload) {
        context.commit(SET_USER, payload.user);
        context.dispatch(RESTART_SESSION, payload.elapsed);
      },
      [DEFINE_USER_PICTURE_PROFILE](context, payload) {
        context.commit(SET_USER_KEY, { key: 'picture', data: payload.picture });
      },
      [DEFINE_UNAUTH_REDIRECT]({ commit }, payload) {
        commit(SET_UNAUTH_REDIRECT, payload.next);
      },
      [CLEAR_UNAUTH_REDIRECT]({ commit }) {
        commit(UNSET_UNAUTH_REDIRECT);
      },
      [DEFINE_CAMPAIGN_URL]({ commit }, payload) {
        commit(SET_CAMPAIGN_URL, payload);
      },
      [LOGIN](context, payload) {
        context.dispatch(DEFINE_ALERTS, { alerts: payload.alerts });
        context.dispatch(DEFINE_DOCUMENTS, { docs: payload.docs });
        context.dispatch(DEFINE_ENTITIES, { entities: payload.entities });
        context.dispatch(DEFINE_MISSING_INDICATORS, { missing: payload.missing });
        context.dispatch(DEFINE_NOTIFICATIONS, { notifications: payload.notifications });
        context.dispatch(DEFINE_PAYMENT_METHODS, { paymentMethods: payload.paymentMethods });
        context.dispatch(DEFINE_PROFILE, { profile: payload.profile });
        context.dispatch(DEFINE_PERMISSIONS, { permissions: payload.permissions });
        context.dispatch(DEFINE_USER, { user: payload.user, elapsed: payload.elapsed || 0 });
        context.dispatch(DEFINE_ACCOUNT_EXECUTIVE, { accountExecutive: payload.accountExecutive });
      },
      [LOGOUT](context, payload) {
        context.dispatch(CLEAR_ALERTS);
        context.dispatch(CLEAR_DOCUMENTS);
        context.dispatch(CLEAR_ENTITIES);
        context.dispatch(CLEAR_MISSING_INDICATORS);
        context.dispatch(CLEAR_NOTIFICATIONS);
        context.dispatch(CLEAR_PAYMENT_METHODS);
        context.dispatch(CLEAR_PROFILE);
        context.dispatch(CLEAR_PERMISSIONS);
        context.dispatch(CLEAR_USER);
        context.dispatch(CLEAR_ACCOUNT_EXECUTIVE);

        context.dispatch(CLEAR_SESSION_TIMEOUT);

        context.dispatch(DEFINE_UNAUTH_REDIRECT, { next: payload ? payload.route : null });
      },
      [RECOVER_DATA](context) {
        let alerts = null;
        let docs = [];
        let entities = [];
        let paymentMethods = [];
        let notifications = [];
        let profile = null;
        let permissions = [];
        let user = null;
        let missing = null;
        let accountExecutive = null;
        try {
          user = JSON.parse(localStorage.user);
          profile = JSON.parse(localStorage.profile);
          alerts = JSON.parse(localStorage.alerts);
          docs = JSON.parse(localStorage.docs);
          entities = JSON.parse(localStorage.entities);
          missing = JSON.parse(localStorage.missing);
          paymentMethods = JSON.parse(localStorage.paymentMethods);
          notifications = JSON.parse(localStorage.notifications);
          permissions = JSON.parse(localStorage.permissions);
          accountExecutive = JSON.parse(localStorage.accountExecutive);
        } catch (_) { /* Ignore if user is not localStorage. */ }

        if (user && user.lastLogin) {
          const elapsed = Math.abs(new Date() - new Date(user.lastLogin));
          if (elapsed <= context.getters.sessionTime.max) {
            const payload = {
              alerts,
              docs,
              entities,
              missing,
              paymentMethods,
              notifications,
              profile,
              permissions,
              user,
              elapsed,
              accountExecutive,
            };
            context.dispatch(LOGIN, payload);
          } else {
            context.dispatch(LOGOUT);
            router.push({ name: 'Index' });
          }
        }
      },
      [RESTART_SESSION](context, elapsedTime) {
        context.dispatch(CLEAR_SESSION_TIMEOUT);
        context.dispatch(DEFINE_SESSION_TIMEOUT, elapsedTime);
      },
      [DEFINE_SESSION_TIMEOUT](context, elapsedTime) {
        const elapsed = elapsedTime || 0;
        const sessionTime = context.getters.sessionTime;
        const timeoutId = window.setTimeout(
          () => { context.dispatch(ALERT_SESSION_ENDING); },
          sessionTime.max - sessionTime.toDecide - elapsed,
        );

        context.commit(SET_SESSION_TIMEOUT, timeoutId);
      },
      [CLEAR_SESSION_TIMEOUT]({ commit }) {
        commit(UNSET_SESSION_TIMEOUT);
      },
      [ALERT_SESSION_ENDING](context) {
        let timerInterval = null;
        Swal.fire({
          title: 'You\'re about to be logged out.',
          html: `
            For security reasons, your connection times out after you've been
            inactive for a while. Click Continue to stay signed in.
            <br/><span></span>.
          `,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Continue',
          cancelButtonText: 'Leave',
          allowOutsideClick: false,
          allowEscapeKey: false,
          timer: context.getters.sessionTime.toDecide,
          onBeforeOpen: () => {
            timerInterval = setInterval(() => {
              const remainder = parseInt(Swal.getTimerLeft() / 1000, 10);
              const text = remainder === 0 ?
                'Your session is over, see you soon!' :
                `Time remaining: ${remainder} seconds`;
              Swal.getContent().querySelector('span').textContent = text;
            }, 100);
          },
          preConfirm: () => {
            const req = new Requests();
            req.get(`/users/${context.getters.user.id}`)
              .then((response) => {
                context.dispatch(DEFINE_USER, { user: response.data.user });
              })
              .catch(() => {
                Swal.fire({
                  title: 'Oops...',
                  text: 'It seems that something was wrong while retrieving your session.' +
                    ' Please log in to recover access to the portal.',
                  icon: 'error',
                }).then(() => {
                  context.dispatch(LOGOUT);
                });
              });
          },
          onClose: () => {
            clearInterval(timerInterval);
          },
        }).then((data) => {
          const dismiss = data.dismiss;

          if (dismiss) {
            const userLogout = dismiss === 'cancel';
            const payload = !userLogout ? { route: router.app.$route.path } : null;
            context.dispatch(LOGOUT, payload);

            /* Display a feedback message if the user chooses to finish their session */
            if (userLogout) {
              Swal.fire({ html: '<div style="font-size: large;">See you soon!</div>' });
            }
          }
        });
      },
    },
  });
}

export { createVuexStore as default };



// WEBPACK FOOTER //
// ./src/vuex/vuex.js